import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { Phone, MapPin, Globe, ArrowUp, Lock } from "lucide-react";
import { Link } from "react-router-dom";
import { LogoIcon } from "../LogoWithText";
import { useLanguage } from "@/context/LanguageContext";

const defaultContent = {
  phone: "718-916-1401",
  address: "820 E 168TH ST BRONX NY 10459",
  website: "globalmetafora.com"
};

const Footer = ({ content }) => {
  const data = { ...defaultContent, ...content };
  const currentYear = new Date().getFullYear();
  const footerRef = useRef(null);
  const isInView = useInView(footerRef, { once: true, margin: "-50px" });
  const { t } = useLanguage();

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <footer ref={footerRef} data-testid="footer" className="relative overflow-hidden">
      {/* Brand watermark in footer */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none select-none">
        <div className="absolute top-8 left-0 right-0 opacity-[0.02]">
          <div className="whitespace-nowrap text-[100px] font-black text-white tracking-wider text-center">
            GLOBAL METAFORA APP
          </div>
        </div>
      </div>

      {/* Animated top border */}
      <motion.div
        className="h-px w-full"
        style={{
          background: "linear-gradient(90deg, transparent, #3b82f6, #06b6d4, #8b5cf6, transparent)"
        }}
        initial={{ scaleX: 0 }}
        animate={isInView ? { scaleX: 1 } : {}}
        transition={{ duration: 1.5 }}
      />

      <div className="bg-gradient-to-t from-[#0b1221] to-[#02040a] py-16">
        <div className="section-container">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
            {/* Brand */}
            <motion.div
              className="lg:col-span-2"
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6 }}
            >
              <motion.div
                className="flex items-center gap-3 mb-4"
                whileHover={{ x: 5 }}
                transition={{ duration: 0.2 }}
              >
                <LogoIcon size={52} />
                <div>
                  <span className="text-white font-bold tracking-wide text-xl block">
                    GLOBAL <span className="text-[#3b82f6]">METAFORA</span> APP
                  </span>
                  <span className="text-[#3b82f6]/60 text-[10px] font-semibold tracking-[0.3em] uppercase">
                    Sistema Operativo Empresarial
                  </span>
                </div>
              </motion.div>
              <p className="text-[#94a3b8] mb-6 max-w-md">
                Más de 20 años desarrollando sistemas operativos integrales que transforman la manera en que las empresas operan.
              </p>
              <div className="flex items-center gap-4">
                <a href={`tel:${data.phone}`} className="flex items-center gap-2 text-[#94a3b8] hover:text-[#3b82f6] transition-colors">
                  <Phone className="w-4 h-4" />
                  <span>{data.phone}</span>
                </a>
              </div>
            </motion.div>

            {/* Links */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.15 }}
            >
              <h4 className="text-white font-semibold mb-4">{t("footer_quick_links")}</h4>
              <ul className="space-y-3">
                {[
                  { label: t("nav_services"), href: "#servicios" },
                  { label: t("nav_sectors"), href: "#sectores" },
                  { label: t("nav_pricing"), href: "#precios" },
                  { label: t("nav_testimonials"), href: "#testimonios" },
                  { label: "FAQ", href: "#faq" },
                ].map((link, i) => (
                  <li key={link.href}>
                    <motion.button
                      onClick={() => scrollToSection(link.href)}
                      className="text-[#94a3b8] hover:text-[#3b82f6] transition-colors inline-flex items-center gap-1 group"
                      whileHover={{ x: 5 }}
                    >
                      <span className="w-0 group-hover:w-3 h-px bg-[#3b82f6] transition-all duration-300" />
                      {link.label}
                    </motion.button>
                  </li>
                ))}
                <li>
                  <Link
                    to="/admin"
                    data-testid="footer-admin-link"
                    className="text-[#64748b] hover:text-[#3b82f6] transition-colors inline-flex items-center gap-2 group"
                  >
                    <Lock size={12} />
                    Admin
                  </Link>
                </li>
              </ul>
            </motion.div>

            {/* Contact */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <h4 className="text-white font-semibold mb-4">{t("footer_contact")}</h4>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-[#3b82f6] flex-shrink-0 mt-0.5" />
                  <span className="text-[#94a3b8]">{data.address}</span>
                </li>
                <li className="flex items-start gap-3">
                  <Phone className="w-5 h-5 text-[#3b82f6] flex-shrink-0 mt-0.5" />
                  <span className="text-[#94a3b8]">{data.phone}</span>
                </li>
                <li className="flex items-start gap-3">
                  <Globe className="w-5 h-5 text-[#3b82f6] flex-shrink-0 mt-0.5" />
                  <a href={`https://${data.website}`} target="_blank" rel="noopener noreferrer" className="text-[#94a3b8] hover:text-[#3b82f6] transition-colors">
                    {data.website}
                  </a>
                </li>
              </ul>
            </motion.div>
          </div>

          {/* Bottom */}
          <motion.div
            className="border-t border-white/5 pt-8 flex flex-col md:flex-row items-center justify-between gap-4"
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : {}}
            transition={{ delay: 0.5 }}
          >
            <p className="text-[#94a3b8] text-sm">
              &copy; {currentYear} Global Metafora App. {t("footer_rights")}
            </p>
            <div className="flex items-center gap-4">
              <p className="text-[#94a3b8] text-sm">
                <span className="text-[#3b82f6]">Ing. Eriberto Fernandez Aquino</span> — {t("footer_director")}
              </p>
              <motion.button
                onClick={scrollToTop}
                className="w-10 h-10 glass-card flex items-center justify-center text-[#94a3b8] hover:text-[#3b82f6] transition-colors"
                whileHover={{ y: -3, borderColor: "rgba(59,130,246,0.5)" }}
                whileTap={{ scale: 0.9 }}
                data-testid="scroll-to-top"
              >
                <ArrowUp className="w-4 h-4" />
              </motion.button>
            </div>
          </motion.div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
